import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

import { useAuthContext } from "~/contexts/auth";
import useSubscriptions from "~/hooks/use-subscriptions";
import { ApiUtils } from "~/shared/api/api-utils.class";
import { basicAnimationStates } from "~/utils/animation-utils";
import Constants from "~/utils/constants";
import imageUtils from "~/utils/image-utils";

import Button from "../common/button";
import Dialog from "../common/dialog";
import CaptainWalletButton from "./captain-wallet-button";
import styles from "./qrcode-dialog.module.scss";

type PropsQRCodeDialog = {
  isOpen?: boolean;
  onClose: () => void;
  onAfterOpen?: () => void;
  onAfterClose?: () => void;
};

declare global {
  var navigator: Navigator;
}

export default function QRCodeDialog({ isOpen, onClose, onAfterOpen, onAfterClose }: PropsQRCodeDialog) {
  const t = useTranslations();
  const { loyaltyPoints, isUserSubscribedToLoyalty, loyaltyIdentifier, currentLoyaltyTier } = useSubscriptions();
  const { user } = useAuthContext();
  const [isCopied, setIsCopied] = useState(false);
  const qrCodeSize = 144;
  const getQrCodeUrl = (value: string) => {
    return ApiUtils.interpolateParams(process.env.NEXT_PUBLIC_QRCODE_URL || "", {
      width: qrCodeSize,
      height: qrCodeSize,
      value,
    });
  };

  useEffect(() => {
    if (isCopied) {
      setTimeout(() => {
        setIsCopied(false);
      }, Constants.COPIED_MESSAGE_TIMEOUT);
    }
  }, [isCopied]);

  useEffect(() => {
    //print single page. Set the position for an edge case of safari.
    window.addEventListener("beforeprint", () => {
      document.documentElement.style.overflow = "hidden";
      document.body.style.position = "relative";
    });
    window.addEventListener("afterprint", () => {
      document.documentElement.style.overflow = "visible";
      document.body.style.position = "static";
    });
  });

  return (
    <Dialog
      isOpen={isOpen}
      title={t("generic.myLoaltyCodeTitle")}
      onClose={onClose}
      onAfterOpen={onAfterOpen}
      onAfterClose={onAfterClose}
      customStyles={styles}
    >
      <div className={styles.myLevelWrapper}>
        <p className={styles.nameWrapper}>
          {user?.FirstName} {user?.LastName}
        </p>
        {isUserSubscribedToLoyalty && (
          <>
            <p className={styles.gradient}>{currentLoyaltyTier?.Name || t("loyalty.basicLevel")}</p>
            <p className={styles.points}>
              {t("generic.loyaltyPoints", {
                points: loyaltyPoints ?? "",
              })}
            </p>
          </>
        )}
      </div>
      {isUserSubscribedToLoyalty && loyaltyIdentifier ? (
        <>
          <Image
            src={getQrCodeUrl(loyaltyIdentifier)}
            alt={loyaltyIdentifier || t("generic.qrCodeImageAlt")}
            width={qrCodeSize}
            height={qrCodeSize}
            loader={imageUtils.evaImageLoader}
            className={styles.qrCode}
          />
          <div className={styles.walletLine}>
            <CaptainWalletButton />
          </div>
          <div className={styles.copyLine}>
            <p className={styles.text}>{loyaltyIdentifier}</p>

            <div className={styles.copyToClipboard}>
              <Button
                lookAsLink
                className={styles.link}
                onClick={() => {
                  navigator.clipboard.writeText(loyaltyIdentifier || "");
                  setIsCopied(true);
                }}
              >
                {t("generic.copyLoyaltyIdentifier")}
              </Button>
              <AnimatePresence>
                {isCopied && (
                  <motion.p
                    initial={basicAnimationStates.off}
                    animate={basicAnimationStates.on}
                    exit={basicAnimationStates.off}
                    role="alert"
                    className={styles.copyToClipboardParagraph}
                  >
                    {t("generic.copied")}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
          </div>
          <Button lookAsLink className={styles.link} onClick={() => window.print()}>
            {t("generic.printPDF")}
          </Button>
        </>
      ) : (
        <p className={styles.text}>{t("error.noUserSubscriptionsFound")}</p>
      )}
    </Dialog>
  );
}
